import { Alert, App, Button, Modal, Space, Typography } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import type { AdminAccountCredentials } from './types';

interface Props {
  open: boolean;
  title: string;
  credentials: AdminAccountCredentials | null;
  onClose: () => void;
  intro?: string;
}

/**
 * One-shot dialog showing a freshly generated/reset cleartext credential.
 * The password is NEVER fetchable again after this dialog closes — operator
 * must copy it now and pass it to the tenant out-of-band.
 */
export function CredentialsModal({ open, title, credentials, onClose, intro }: Props) {
  const { message } = App.useApp();

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success(`${label} 已复制到剪贴板`);
    } catch {
      message.error('复制失败，请手动选择文本');
    }
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="ok" type="primary" onClick={onClose}>
          我已记录
        </Button>,
      ]}
      maskClosable={false}
      destroyOnClose
      width={520}
    >
      {credentials ? (
        <>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="该明文密码仅本次显示，关闭后无法再次查看"
            description={intro ?? '请在关闭对话框前复制并妥善保管，并通过线下渠道告知租户。'}
          />
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <CredentialField
              label="租户 ID（登录账号）"
              value={credentials.username}
              onCopy={(v) => copy('账号', v)}
            />
            <CredentialField
              label="初始密码"
              value={credentials.clearTextPassword}
              onCopy={(v) => copy('密码', v)}
              monospace
            />
          </Space>
        </>
      ) : (
        <Typography.Paragraph type="secondary">未生成凭据。</Typography.Paragraph>
      )}
    </Modal>
  );
}

interface CredentialFieldProps {
  label: string;
  value: string;
  onCopy: (value: string) => void;
  monospace?: boolean;
}

function CredentialField({ label, value, onCopy, monospace }: CredentialFieldProps) {
  return (
    <div>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Typography.Text>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 4,
          padding: '8px 12px',
          background: '#fafafa',
          border: '1px solid var(--mall-color-border)',
          borderRadius: 6,
        }}
      >
        <Typography.Text
          copyable={false}
          style={{
            flex: 1,
            fontFamily: monospace ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' : undefined,
            fontSize: monospace ? 16 : undefined,
            wordBreak: 'break-all',
          }}
        >
          {value}
        </Typography.Text>
        <Button size="small" icon={<CopyOutlined />} onClick={() => onCopy(value)}>
          复制
        </Button>
      </div>
    </div>
  );
}
